import './Landing.scss';

import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { getAurigaSettings } from '@utils/cookies';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import AurigaConstellationPNG from '../../assets/images/AurigaConstellation_Small.png';

/**
 * Landing component
 *
 * This component serves as the default landing page for the application while in development.
 * Can be modified as needed.
 * @returns {ReactNode} The Landing component with a welcome message and navigation cards
 * @constructor
 */
const Landing = (): ReactNode => {
  // Parsed Auriga Settings to add in Bridging endpoint (if exists)
  const aurigaSettings = getAurigaSettings();
  useRecordLoadComplete(true);
  const bridgingEndpoint = aurigaSettings?.appParams?.bridgingAppEndpoint
    ? aurigaSettings.appParams.bridgingAppEndpoint
    : '#';
  const navigate = useNavigate();
  const aurigaSmallLogo = (
    <img
      src={AurigaConstellationPNG}
      alt='Auriga Constellation Logo'
      height='230px'
      width='auto'
      className='auriga-constellation-logo'
    />
  );

  return (
    <Box className='landing'>
      <Box className='landing-box'>
        <Grid container spacing={2} className='auriga-intro-container'>
          <Grid size={{ xs: 2 }} /> {/* Spacer */}
          {/* Left Auriga Intro Section */}
          <Grid size={{ xs: 10, md: 5 }}>
            <Typography variant='body1'>Corp FP&A</Typography>
            <Typography variant='h3'>Auriga</Typography>
            <Typography variant='h6'>Collection of Tools</Typography>
            <Typography variant='body1'>
              Auriga is a collaborative platform that automates the creation of decks and reports, generating executive
              summaries and custom views based on actual results and forecasts. The system allows team members to
              collaborate with features for document editing, commenting, and KPI management.
            </Typography>
          </Grid>
          {/* Right Auriga Intro Section */}
          <Grid size={{ xs: 8, md: 3 }}>{aurigaSmallLogo}</Grid>
          <Grid size={{ xs: 2 }} /> {/* Spacer */}
        </Grid>
        <Grid size={{ xs: 1 }} /> {/* Spacer */}
      </Box>

      <Box className='card-section'>
        <Grid container spacing={3} className='card-container'>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card className='card -active'>
              <CardActionArea onClick={() => navigate('/deck-preparation')}>
                <CardContent>
                  <Typography variant='h5' className='card-title'>
                    Deck Preparation
                  </Typography>
                  <Typography variant='body2' className='card-description'>
                    A reporting platform that automates executive deck for actual and planning results, while also
                    supporting customizable, dynamic reporting views for various business needs.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card className='card -active'>
              <CardActionArea
                className='card-action-area'
                onClick={() => {
                  window.location.href = bridgingEndpoint;
                }}
              >
                <CardContent>
                  <Typography variant='h5' className='card-title'>
                    Bridging
                  </Typography>
                  <Typography variant='body2' className='card-description'>
                    Bridging solution that optimizes narrative capture across business segments for P&I.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Landing;
